import { useState } from 'react';
import type { Options, StateNames, States } from './Configurator';

/**
 * Hook qui gère l'état d'affichage du configurateur :
 * options visuelles, textures, points, mode avancé, etc.
 * Fournit setValue/getValue pour un accès générique par nom d'état.
 */
export const useConfiguratorState = (initialModelId: number) => {
  const [options, setOptions] = useState<Options>({
    backgroundColor: '#EAECF0',
    light: 0.5,
  });
  const [hasActiveTexture, setHasActiveTexture] = useState<boolean>(false);
  const [hasPoints, setHasPoints] = useState<boolean>(false);
  const [allPieces, setAllPieces] = useState<boolean>(true);
  const [hasAdvancedMode, setHasAdvancedMode] = useState<boolean>(false);
  const [hasWired, setHasWired] = useState<boolean>(false);
  const [hasAdvancedMove, setHasAdvancedMove] = useState<boolean>(true);
  const [modelId, setModelId] = useState<number>(initialModelId);

  const setValue = <T extends StateNames>(stateName: T, value: States[T]) => {
    switch (stateName) {
      case 'options': {
        setOptions(value as Options);
        break;
      }
      case 'texture': {
        setHasActiveTexture(value as boolean);
        break;
      }
      case 'points': {
        setHasPoints(value as boolean);
        break;
      }
      case 'hasAdvancedMode': {
        setHasAdvancedMode(value as boolean);
        break;
      }
      case 'allPieces': {
        setAllPieces(value as boolean);
        break;
      }
      case 'wired': {
        setHasWired(value as boolean);
        break;
      }
      case 'modelId': {
        setModelId(value as number);
        break;
      }
      case 'advancedMove': {
        setHasAdvancedMove(value as boolean);
        break;
      }
      default:
    }
  };

  const getValue = <T extends StateNames>(stateName: T): States[T] => {
    switch (stateName) {
      case 'options': {
        return options as States[T];
      }
      case 'texture': {
        return hasActiveTexture as States[T];
      }
      case 'points': {
        return hasPoints as States[T];
      }
      case 'allPieces': {
        return allPieces as States[T];
      }
      case 'hasAdvancedMode': {
        return hasAdvancedMode as States[T];
      }
      case 'wired': {
        return hasWired as States[T];
      }
      case 'advancedMove': {
        return hasAdvancedMove as States[T];
      }
      case 'modelId': {
        return modelId as States[T];
      }
      default: {
        throw new Error('State not found');
      }
    }
  };

  return {
    setValue,
    getValue,
    hasAdvancedMove,
  };
};
